import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { CATEGORY_GROUPS, matchesGroup } from "@/components/Products";

export const FEATURED_IDS = [
  "pet-cats-and-dogs-portable-outdoor-drinking-cups",
  "120v-600w-75mm-caliber-1000ml-slag-cup-3-gear-electric-juicer",
  "slushy-maker-portable-travel-ice-cup-homemade-freeze-drinks-cup",
  "mini-portable-blender-electric-juicer-fruit-mixers-fruit-extractors",
  "led-light-up-dog-collar",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function PopularProducts() {
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const { add, setOpen } = useCart();
  const navigate = useNavigate();

  const popular = useMemo(() => {
    if (!products.length) return [];
    const picked = FEATURED_IDS.map((id) =>
      products.find((p) => p.id === id)
    ).filter(Boolean);
    const seen = new Set(picked.map((p) => p.id));
    for (const group of Object.keys(CATEGORY_GROUPS)) {
      if (picked.length === 5) break;
      const hit = products.find((p) => matchesGroup(p, group) && !seen.has(p.id));
      if (hit) {
        picked.push(hit);
        seen.add(hit.id);
      }
    }
    let i = 0;
    while (picked.length < 5 && i < products.length) {
      if (!seen.has(products[i].id)) {
        picked.push(products[i]);
        seen.add(products[i].id);
      }
      i += 1;
    }
    return picked;
  }, [products]);

  if (popular.length < 5) return null;

  const handleAdd = (product) => {
    add(product);
    toast(`${product.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  return (
    <section
      data-testid="popular-section"
      className="mx-auto max-w-[1600px] px-5 py-10 md:px-12 md:py-20"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-8 flex flex-wrap items-end justify-between gap-4 md:mb-12"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-smoke">
            Popular right now
          </p>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl md:text-6xl">
            Most <span className="italic text-ember">loved</span> this week.
          </h2>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-smoke">
          Hand-picked favorites
        </p>
      </motion.div>

      <div className="grid grid-cols-2 gap-3.5 sm:gap-4 lg:auto-rows-[260px] lg:grid-cols-4">
        {popular.map((product, i) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: isTouch ? 0 : i * 0.08, ease: EASE }}
            data-testid={`popular-card-${product.id}`}
            onClick={() => navigate(`/product/${product.id}`)}
            className={`group relative cursor-pointer overflow-hidden bg-white ${
              i === 0
                ? "col-span-2 aspect-[2/1] lg:aspect-auto lg:row-span-2"
                : "aspect-square lg:aspect-auto"
            }`}
          >
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent transition-colors duration-500 group-hover:from-ember/70" />
            <span className="absolute left-3 top-3 bg-ember px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-ink sm:left-4 sm:top-4 sm:px-3 sm:py-1 sm:text-[10px]">
              {product.tag}
            </span>
            {product.available === false ? (
              <span
                data-testid={`sold-out-${product.id}`}
                className="absolute bottom-3 right-3 z-20 bg-ink px-3 py-1.5 text-[9px] uppercase tracking-[0.2em] text-bone sm:bottom-4 sm:right-4 sm:px-4 sm:py-2 sm:text-[10px]"
              >
                Sold out
              </span>
            ) : (
              <button
                data-testid={`popular-add-${product.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdd(product);
                }}
                aria-label={`Add ${product.name} to bag`}
                className="absolute bottom-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-bone text-ink transition-all duration-300 hover:scale-110 hover:bg-ember sm:bottom-4 sm:right-4 sm:h-11 sm:w-11"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.5} />
              </button>
            )}
            <div className="absolute bottom-0 left-0 z-10 max-w-[calc(100%-3rem)] p-3 sm:p-4 md:p-5">
              <p
                className={`line-clamp-2 font-serif font-medium leading-tight tracking-tight text-bone ${
                  i === 0 ? "text-xl sm:text-2xl md:text-4xl" : "text-base sm:text-lg md:text-xl"
                }`}
              >
                {product.name}
              </p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.15em] text-ember sm:text-xs sm:text-bone/80">
                {fmt(product.price)}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
